import { createTheme } from '@mui/material/styles';
import { ColorPartial } from '@mui/material/styles/createPalette';
import { TypographyOptions } from '@mui/material/styles/createTypography';
import type {} from '@mui/x-data-grid/themeAugmentation';

declare module '@mui/material/styles/createPalette' {
  interface CommonColors {
    blue: ColorPartial;
    brown: ColorPartial;
    gray: ColorPartial;
    white: string;
    black: string;
    purple: ColorPartial;
    orange: ColorPartial;
    green: ColorPartial;
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    subscription: {
      green: string;
      greenHover: string;
      badgeExpired: string;
      sectionBg: string;
      cardBorder: string;
      buttonBorder: string;
      usageTrack: string;
      textSecondary: string;
    };
  }
  interface PaletteOptions {
    subscription?: Palette['subscription'];
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h7: true;
  }
}

interface ExtendedTypographyOptions extends TypographyOptions {
  h7: React.CSSProperties;
}

export const green: ColorPartial = {
  50: '#F0F5EB',
  100: '#E4EDD8',
  200: '#aed581',
  300: '#8BC34A',
  500: '#689f38',
  700: '#316900',
};

export const blue: ColorPartial = {
  50: '#1931841A',
  200: '#64b5f6',
  300: '#2196f3',
  500: '#22bccf',
};

export const brown: ColorPartial = {
  50: '#F7F1E7',
  100: '#F4F0EF',
  500: '#a2845e45',
  600: '#886C4A',
  800: '#a2845e',
  900: '#735229',
};

export const purple: ColorPartial = {
  500: '#D0D2FF',
};

/** Grey shades of the app palette. */
export const gray: ColorPartial = {
  100: '#F4F4F4',
  200: '#eeeeee',
  300: '#EAEAEA',
  400: '#D0D0D0',
  500: '#999999',
  600: 'rgba(0, 0, 0, 0.74)',
  700: '#333333',
};

export const orange: ColorPartial = {
  500: '#F5A623',
};

/** Material UI theme of the React app. */
export const theme = createTheme({
  palette: {
    primary: {
      dark: green[700]!,
      main: green[500]!,
      light: green[200],
    },
    secondary: {
      main: '#9e9e9e',
    },
    text: {
      disabled: '#808080',
    },
    info: {
      main: '#EFF2E4',
      light: '#F1F1EA',
      dark: '#001F24',
    },
    error: {
      main: '#e9514a',
    },
    warning: {
      main: '#FFA726',
    },
    common: {
      blue,
      brown,
      gray,
      white: '#ffffff',
      black: '#000000',
      purple,
      orange,
      green,
    },
    subscription: {
      green: '#2f6b0f',
      greenHover: '#26580c',
      badgeExpired: '#ba1a1a',
      sectionBg: '#f0f0f0',
      cardBorder: 'rgba(0, 0, 0, 0.1)',
      buttonBorder: 'rgba(0, 0, 0, 0.15)',
      usageTrack: '#d8dbd0',
      textSecondary: 'rgba(0, 0, 0, 0.55)',
    },
  },
  typography: {
    h7: {
      fontSize: '1.125rem',
      fontWeight: 400,
    },
  } as ExtendedTypographyOptions,
  components: {
    MuiTypography: {
      styleOverrides: {
        h1: {
          color: 'primary.main',
          fontSize: '2.2rem',
          fontWeight: 400,
        },
        h2: {
          color: 'text.primary',
          fontSize: '2rem',
          fontWeight: 400,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontFamily: 'Roboto',
          letterSpacing: '0.5px',
          lineHeight: '36px',
          padding: '0px 28px',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        circle: {
          color: 'primary.main',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          marginBottom: '2.125rem',
          marginTop: '1.5rem',
        }),
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.info.dark,
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          justifyContent: 'flex-start',
          gap: '1.5rem',
          paddingTop: '1.7rem',
          paddingBottom: '1.5rem',
          paddingLeft: '1.5rem',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        groupLabel: ({ theme }) => ({
          backgroundColor: theme.palette.info.main,
          fontSize: '1rem',
          color: theme.palette.info.dark,
          fontWeight: 400,
        }),
      },
    },
  },
});
